<div
  bind:this={menuRoot}
  bind:clientWidth={menuRect.width}
  bind:clientHeight={menuRect.height}
  class={ContextMenuComponent.Classes.Component}
  style={`
    left: ${getPositionLeft(menuRect, selectedNodeRect)}px; 
    top: ${getPositionTop(menuRect, selectedNodeRect)}px;
  `}
>

  <div class={ContextMenuComponent.Classes.ActionsContainer}>
    {#if $menuProps.actions}
      {#each Object.values($menuProps.actions) as action}
        {#if contextMenuActionIsVisible(action.action)}
          <span
            class={ContextMenuComponent.Classes.ActionName}
            on:click={() => eventHandlers.handleContextMenuAction(action.action)}
          >
            {contextMenuActionName(action.action)}
          </span>
        {/if}
      {/each}
    {/if}
  </div>

  <div class={ContextMenuComponent.Classes.AnnotationsContainer}>
    {#each annotations as annotation}
      <span
        class={`${ContextMenuComponent.Classes.Annotation} ${appendIf({ blank: annotation.action === undefined })}`}
        title={annotation.meaning}
        on:click={() => annotation.action !== undefined && eventHandlers.handleContextMenuAction(
          annotation.action,
          {
            data: { annotation: annotation.display },
          },
        )}
      >
        {annotation.display}
      </span>
    {/each}
  </div>
</div>

<svelte:body on:click={handleOutsideClick} />

<script lang="ts">
import { contextMenuAnnotations as annotations } from 'chessboard/model/annotation/config';
import { ContextMenuComponent } from 'components/move-list/components/context-menu/config/types';
import type { ContextShape, MenuProps, NodeKeys } from 'components/move-list/store/types';
import type { Data } from 'src/utils/svelte/data';
import { appendIf } from 'src/utils/svelte/assertions';
import { getContext } from 'svelte';
import type { GameAPI } from 'chessboard/types';
import { MoveListPlugin } from 'chessboard/plugins/move-list/config/types';
import { getPositionLeft, getPositionTop } from '../../helpers/get-menu-position';

export let selectedNodeKeys: Data<NodeKeys | null>;
export let selectedNodeRect: DOMRect;
export let menuProps: Data<MenuProps>;
export let game: GameAPI;

const { store, eventHandlers } = getContext<ContextShape>(MoveListPlugin.Name);
const { translations } = store;

const menuRect = { width: 0, height: 0 }; // bound on mount (read only)
let menuRoot: HTMLElement | null = null; // bound on mount (read only)

/**
 * Hide the context menu when a document click is not within the menu
 */
function handleOutsideClick(event) {
  if (menuRoot === null || menuRoot.contains(event.target)) {
    return;
  }

  eventHandlers.closeContextMenu();
}

/**
 * Check if the action is visible
 */
function contextMenuActionIsVisible(action): boolean {
  const actions = menuProps.get().actions;
  const keys = selectedNodeKeys.get();
  const isVisibleFn = actions?.[action]?.isVisible;
  if (!keys || !isVisibleFn) {
    return false;
  }

  const node = game.getNodeByIds(keys.ids);
  if (!node) {
    return false;
  }

  return isVisibleFn({ node, game });
}

/**
 * Get the action name
 */
function contextMenuActionName(action): string {
  const actions = menuProps.get().actions;
  const keys = selectedNodeKeys.get();
  const nameFn = actions?.[action]?.getDisplayName;

  if (!keys || !nameFn) {
    return '';
  }

  const node = game.getNodeByIds(keys.ids);
  if (!node) {
    return '';
  }

  const name = nameFn({ node, game });

  return $translations[name] ? $translations[name]() : name;
}
</script>

<style src="./context-menu.scss"></style>
